import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getEligibleAccounts, createStatementRun } from "../../api";
import "./BulkStatements.css";

function BulkStatementModal({ show, onHide, practiceNumbers, onRunCreated }) {
  // Settings
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [includeExtraEmails, setIncludeExtraEmails] = useState(true);
  const [includeAllTransactions, setIncludeAllTransactions] = useState(false);

  // Practice filter (only relevant when user has multiple practices)
  const [practiceFilter, setPracticeFilter] = useState("all");

  // Resolved list of practice numbers to use for all API calls
  const effectivePracticeNumbers =
    practiceFilter === "all" || practiceNumbers.length <= 1
      ? practiceNumbers
      : [practiceFilter];

  // Account list state
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [sortKey, setSortKey] = useState("totalOutstanding");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false);

  // Per-account removed emails: { accountId: Set<email> }
  const [removedEmails, setRemovedEmails] = useState({});

  // Submission state
  const [submitting, setSubmitting] = useState(false);

  const totalPages = Math.ceil(total / limit);

  const fetchAccounts = useCallback(async () => {
    if (!practiceNumbers?.length || !show) return;
    setLoading(true);
    try {
      const data = await getEligibleAccounts({
        practiceNumbers: effectivePracticeNumbers,
        month,
        page,
        limit,
        sortKey,
        sortDir,
      });
      setAccounts(data.accounts || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Error fetching eligible accounts:", err);
    } finally {
      setLoading(false);
    }
  }, [practiceNumbers, practiceFilter, show, page, limit, sortKey, sortDir, month]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setSelectedIds(new Set());
      setSelectAllPages(false);
      setPage(1);
      setRemovedEmails({});
      setPracticeFilter("all");
    }
  }, [show]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
    setPage(1);
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " \u25B2" : " \u25BC";
  };

  const toggleSelect = (accountId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
    setSelectAllPages(false);
  };

  const toggleSelectCurrentPage = () => {
    const currentPageIds = accounts.map((a) => String(a.accountId));
    const allSelected = currentPageIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        currentPageIds.forEach((id) => next.delete(id));
      } else {
        currentPageIds.forEach((id) => next.add(id));
      }
      return next;
    });
    setSelectAllPages(false);
  };

  const handleSelectAll = async () => {
    if (selectAllPages) {
      setSelectedIds(new Set());
      setSelectAllPages(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getEligibleAccounts({
        practiceNumbers: effectivePracticeNumbers,
        month,
        idsOnly: true,
      });
      const allIds = new Set((data.accountIds || []).map(String));
      setSelectedIds(allIds);
      setSelectAllPages(true);
    } catch (err) {
      console.error("Error fetching all accounts:", err);
    } finally {
      setLoading(false);
    }
  };

  const currentPageAllSelected = useMemo(
    () =>
      accounts.length > 0 &&
      accounts.every((a) => selectedIds.has(String(a.accountId))),
    [accounts, selectedIds]
  );

  const selectedTotals = useMemo(() => {
    const selected = accounts.filter((a) => selectedIds.has(String(a.accountId)));
    return {
      patient: selected.reduce((sum, a) => sum + (a.outstandingPatient || 0), 0),
      medicalAid: selected.reduce((sum, a) => sum + (a.outstandingMedicalAid || 0), 0),
      total: selected.reduce((sum, a) => sum + (a.totalOutstanding || 0), 0),
    };
  }, [accounts, selectedIds]);

  /**
   * Get visible emails for an account based on toggle and removals.
   * emails[0] is always the primary email; emails[1..4] are extra.
   */
  const getVisibleEmails = (acc) => {
    const all = acc.emails || [];
    if (all.length === 0) return [];
    const primary = [all[0]];
    if (!includeExtraEmails) return primary;
    const extra = all.slice(1);
    const removed = removedEmails[String(acc.accountId)] || new Set();
    return [...primary, ...extra.filter((e) => !removed.has(e))];
  };

  const handleRemoveEmail = (accountId, email, e) => {
    e.stopPropagation();
    setRemovedEmails((prev) => {
      const next = { ...prev };
      const set = new Set(next[accountId] || []);
      set.add(email);
      next[accountId] = set;
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) return;

    setSubmitting(true);
    try {
      const token = sessionStorage.getItem("user");
      const decoded = token ? jwtDecode(token) : {};

      // Build per-account email overrides for accounts with removed emails
      const emailOverrides = {};
      for (const acc of accounts) {
        const id = String(acc.accountId);
        if (!selectedIds.has(id)) continue;
        const removed = removedEmails[id];
        if (removed && removed.size > 0) {
          emailOverrides[id] = getVisibleEmails(acc);
        }
      }

      const result = await createStatementRun({
        month,
        practiceNumbers: effectivePracticeNumbers,
        selectedAccountIds: Array.from(selectedIds),
        settings: {
          includeExtraEmails,
          includeAllTransactions,
        },
        emailOverrides: Object.keys(emailOverrides).length > 0 ? emailOverrides : undefined,
      });

      if (onRunCreated) onRunCreated(result);
      onHide();
    } catch (err) {
      console.error("Error creating statement run:", err);
      alert("Failed to create statement run. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="bulk-statement-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Bulk Statement Run</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Settings Row */}
        <div className="d-flex flex-wrap gap-3 mb-3 align-items-end">
          <Form.Group style={{ minWidth: 160 }}>
            <Form.Label className="mb-1 small fw-bold">Statement Month</Form.Label>
            <Form.Control
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </Form.Group>

          {practiceNumbers.length > 1 && (
            <Form.Group style={{ minWidth: 180 }}>
              <Form.Label className="mb-1 small fw-bold">Practice</Form.Label>
              <Form.Select
                size="sm"
                value={practiceFilter}
                onChange={(e) => {
                  setPracticeFilter(e.target.value);
                  setPage(1);
                  setSelectedIds(new Set());
                  setSelectAllPages(false);
                }}
              >
                <option value="all">All practices</option>
                {practiceNumbers.map((pn) => (
                  <option key={pn} value={pn}>
                    {pn}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Check
            type="switch"
            id="includeAllTransactions"
            label="All transactions"
            checked={includeAllTransactions}
            onChange={(e) => setIncludeAllTransactions(e.target.checked)}
            className="mt-auto"
            title="Include all transactions from account history up to end of selected month"
          />

          <Form.Check
            type="switch"
            id="includeExtraEmails"
            label="Include extra emails (2-5)"
            checked={includeExtraEmails}
            onChange={(e) => setIncludeExtraEmails(e.target.checked)}
            className="mt-auto"
          />
        </div>

        {/* Selection controls */}
        <div className="d-flex justify-content-between align-items-center mb-2">
  <Form.Label className="mb-0 text-primary text-sm">
    Select the accounts to include in the statement run.
    <br />
    <strong>NOTE:</strong> Additional emails added on the account can be removed by
    clicking the <span className="text-danger fw-bold">×</span> next to the email.
  </Form.Label>

  <div className="d-flex gap-2 align-items-center">
    <Button
      variant="outline-primary"
      size="sm"
      onClick={toggleSelectCurrentPage}
    >
      {currentPageAllSelected ? "Deselect Page" : "Select Page"}
    </Button>

    <Button
      variant="outline-secondary"
      size="sm"
      onClick={handleSelectAll}
    >
      {selectAllPages ? "Deselect All" : `Select All (${total})`}
    </Button>
  </div>
</div>


        {/* Accounts Table */}
        <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
          <Table bordered hover responsive size="sm" className="mb-0">
            <thead className="table-primary text-center sticky-top">
              <tr>
                <th className="align-middle" style={{ width: 40 }}>
                  <Form.Check
                    type="checkbox"
                    checked={currentPageAllSelected}
                    onChange={toggleSelectCurrentPage}
                  />
                </th>
                <th
                  className="align-middle text-primary sortable"
                  onClick={() => handleSort("accountNumber")}
                >
                  Acc #{sortIcon("accountNumber")}
                </th>
                <th
                  className="align-middle text-primary sortable"
                  onClick={() => handleSort("accountFullNames")}
                >
                  Account Name{sortIcon("accountFullNames")}
                </th>
                <th className="align-middle text-primary">Emails</th>
                <th
                  className="text-end align-middle text-primary sortable"
                  onClick={() => handleSort("outstandingPatient")}
                >
                  Patient{sortIcon("outstandingPatient")}
                </th>
                <th
                  className="text-end align-middle text-primary sortable"
                  onClick={() => handleSort("outstandingMedicalAid")}
                >
                  Med Aid{sortIcon("outstandingMedicalAid")}
                </th>
                <th
                  className="text-end align-middle text-primary sortable"
                  onClick={() => handleSort("totalOutstanding")}
                >
                  Total{sortIcon("totalOutstanding")}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Loading...
                  </td>
                </tr>
              ) : accounts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-muted">
                    No accounts with outstanding balances found.
                  </td>
                </tr>
              ) : (
                accounts.map((acc) => {
                  const id = String(acc.accountId);
                  const allEmails = acc.emails || [];
                  const primaryEmail = allEmails[0] || null;
                  const extraEmails = allEmails.slice(1);
                  const removed = removedEmails[id] || new Set();

                  return (
                    <tr
                      key={id}
                      className={selectedIds.has(id) ? "table-active" : ""}
                      onClick={() => toggleSelect(id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center align-middle" onClick={(e) => e.stopPropagation()}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedIds.has(id)}
                          onChange={() => toggleSelect(id)}
                        />
                      </td>
                      <td>{acc.accountNumber}</td>
                      <td>{acc.accountName}</td>
                      <td onClick={(e) => e.stopPropagation()} style={{ minWidth: 200 }}>
                        {!primaryEmail ? (
                          <span className="text-danger small">No email</span>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            <span className="small">{primaryEmail}</span>
                            {includeExtraEmails &&
                              extraEmails.map((email) =>
                                removed.has(email) ? null : (
                                  <span
                                    key={email}
                                    className="small d-flex align-items-center gap-1 text-muted"
                                  >
                                    {email}
                                    <i
                                      className="fa-solid fa-xmark text-danger"
                                      style={{ cursor: "pointer", fontSize: "0.7rem" }}
                                      title="Remove this email"
                                      onClick={(e) => handleRemoveEmail(id, email, e)}
                                    />
                                  </span>
                                )
                              )}
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        R {acc.outstandingPatient?.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {acc.outstandingMedicalAid?.toFixed(2)}
                      </td>
                      <td className="text-end fw-bold">
                        R {acc.totalOutstanding?.toFixed(2)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-2">
            <Pagination size="sm" className="mb-0">
              <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
              <Pagination.Prev
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              />
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <Pagination.Item
                    key={p}
                    active={p === page}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Pagination.Item>
                );
              })}
              <Pagination.Next
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              />
              <Pagination.Last
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div className="text-muted small">
          {selectedIds.size > 0 && (
            <>
              Selected: <strong>{selectedIds.size}</strong> accounts
              {selectedTotals.total > 0 && (
                <>
                  {" | "}Patient: <strong>R {selectedTotals.patient.toFixed(2)}</strong>
                  {" | "}Med Aid: <strong>R {selectedTotals.medicalAid.toFixed(2)}</strong>
                  {" | "}Total: <strong>R {selectedTotals.total.toFixed(2)}</strong>
                </>
              )}
            </>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting || selectedIds.size === 0}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Creating...
              </>
            ) : (
              `Start Run (${selectedIds.size})`
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default BulkStatementModal;
